import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

const ADMIN_PATH = process.env.REACT_APP_ADMIN_SECRET_PATH || "admin-secret-path";

export default function Login() {
  const { user, login, error } = useAuth();
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If already authenticated, jump straight to the dashboard
  useEffect(() => {
    if (user && typeof user === "object") {
      setLocation(`/${ADMIN_PATH}`);
    }
  }, [user, setLocation]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) setLocation(`/${ADMIN_PATH}`);
  };

  return (
    <div data-testid="page-login" className="min-h-screen bg-spa-cream flex flex-col">
      <div className="container-spa pt-10">
        <Link href="/" data-testid="login-back-home">
          <div className="inline-block cursor-pointer">
            <Logo variant="default" size="md" />
          </div>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="card-spa">
            <div className="w-12 h-12 rounded-full bg-spa-green/10 flex items-center justify-center mb-6">
              <Lock className="w-5 h-5 text-spa-green" strokeWidth={1.5} />
            </div>
            <p className="overline mb-3">Owner Access</p>
            <h1 className="font-serif text-3xl text-spa-dark mb-2">Welcome back, Stephanie.</h1>
            <p className="text-sm text-spa-gray mb-8">
              Sign in to view bookings, clients, and gift cards.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-spa-gray mb-1.5 block">Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  className="input-spa"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-spa-gray mb-1.5 block">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  className="input-spa"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                />
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                >
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit"
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
              >
                {submitting ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
              </button>
            </form>

            <p className="text-xs text-spa-gray text-center mt-8">
              Trouble signing in? Contact your website manager.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
